/**
 * Route Safety Planner - A to B route safety analysis
 * Analyzes safety along travel routes with danger zone highlighting
 */
import React, { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Navigation,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  Target,
  Clock,
  Shield,
  ChevronRight,
  Route,
  Flag,
  CircleDot
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Grade colors
const GRADE_COLORS = {
  'A': 'bg-green-500',
  'B': 'bg-teal-500',
  'C': 'bg-amber-500',
  'D': 'bg-orange-500',
  'F': 'bg-red-500'
};

const RouteSegment = ({ segment, index, isLast }) => {
  const gradeColor = GRADE_COLORS[segment.grade] || 'bg-slate-500';
  const hasConcerns = segment.concerns?.length > 0;

  return (
    <div className="relative">
      {/* Connection line */}
      {!isLast && (
        <div className="absolute left-[18px] top-10 bottom-0 w-0.5 bg-slate-700" />
      )}
      
      <div className="flex items-start gap-3">
        {/* Marker */}
        <div className={`w-9 h-9 rounded-full ${gradeColor} flex items-center justify-center flex-shrink-0 z-10`}>
          <span className="text-white text-sm font-bold">{index + 1}</span>
        </div>
        
        {/* Content */}
        <div className="flex-1 pb-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-slate-200">
              Segment {index + 1}
            </span>
            <Badge className={`${gradeColor} text-white text-[10px]`}>
              {segment.grade} ({segment.overall_score})
            </Badge>
          </div>
          
          {/* Coordinates */}
          <p className="text-xs text-slate-500 mb-2">
            {segment.point.lat.toFixed(4)}, {segment.point.lng.toFixed(4)}
          </p>
          
          {/* Concerns */}
          {hasConcerns && (
            <div className="flex flex-wrap gap-1">
              {segment.concerns.map((concern, i) => (
                <Badge 
                  key={i}
                  variant="outline"
                  className="text-[10px] border-orange-500/50 text-orange-400"
                >
                  <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                  {concern.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RecommendationCard = ({ rec }) => {
  const typeConfig = {
    warning: { icon: AlertTriangle, color: 'text-orange-400 bg-orange-500/20 border-orange-500/50' },
    caution: { icon: AlertTriangle, color: 'text-amber-400 bg-amber-500/20 border-amber-500/50' },
    info: { icon: Info, color: 'text-blue-400 bg-blue-500/20 border-blue-500/50' },
    success: { icon: CheckCircle, color: 'text-green-400 bg-green-500/20 border-green-500/50' }
  };
  
  const config = typeConfig[rec.type] || typeConfig.info;
  const Icon = config.icon;

  return (
    <div className={`p-3 rounded-lg border ${config.color}`}>
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium">{rec.message}</p>
          {rec.action && (
            <p className="text-xs opacity-80 mt-1">{rec.action}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const RouteSafetyPlannerCard = () => {
  const { token } = useAuth();
  const [origin, setOrigin] = useState({ lat: '', lng: '' });
  const [destination, setDestination] = useState({ lat: '', lng: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleAnalyze = useCallback(async () => {
    if (!token) return;
    
    const oLat = parseFloat(origin.lat);
    const oLng = parseFloat(origin.lng);
    const dLat = parseFloat(destination.lat);
    const dLng = parseFloat(destination.lng);

    if (isNaN(oLat) || isNaN(oLng) || isNaN(dLat) || isNaN(dLng)) {
      setError('Please enter valid coordinates for both origin and destination');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/safety/route-safety`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          origin: { lat: oLat, lng: oLng },
          destination: { lat: dLat, lng: dLng }
        })
      });

      if (!response.ok) throw new Error('Failed to analyze route');

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message || 'Failed to analyze route safety');
    } finally {
      setLoading(false);
    }
  }, [token, origin, destination]);

  const handleUseCurrentLocation = (target) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            lat: pos.coords.latitude.toFixed(6),
            lng: pos.coords.longitude.toFixed(6)
          };
          if (target === 'origin') {
            setOrigin(coords);
          } else {
            setDestination(coords);
          }
        },
        () => setError('Could not get your location')
      );
    }
  };

  // Sample locations for quick testing
  const quickFill = (type) => {
    if (type === 'la_to_sd') {
      setOrigin({ lat: '34.0522', lng: '-118.2437' });
      setDestination({ lat: '32.7157', lng: '-117.1611' });
    } else if (type === 'sf_to_la') {
      setOrigin({ lat: '37.7749', lng: '-122.4194' });
      setDestination({ lat: '34.0522', lng: '-118.2437' });
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="route-safety-planner">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Route className="w-5 h-5 text-teal-400" />
          Route Safety Planner
        </h3>

        {/* Origin */}
        <div className="mb-4">
          <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
            <CircleDot className="w-3 h-3 text-green-400" />
            Origin
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="Latitude"
              value={origin.lat}
              onChange={(e) => setOrigin(prev => ({ ...prev, lat: e.target.value }))}
              className="bg-slate-800/50 border-slate-700 text-sm"
            />
            <Input
              placeholder="Longitude"
              value={origin.lng}
              onChange={(e) => setOrigin(prev => ({ ...prev, lng: e.target.value }))}
              className="bg-slate-800/50 border-slate-700 text-sm"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleUseCurrentLocation('origin')}
              className="text-slate-400 hover:text-teal-400"
            >
              <Target className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Destination */}
        <div className="mb-4">
          <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
            <Flag className="w-3 h-3 text-red-400" />
            Destination
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="Latitude"
              value={destination.lat}
              onChange={(e) => setDestination(prev => ({ ...prev, lat: e.target.value }))}
              className="bg-slate-800/50 border-slate-700 text-sm"
            />
            <Input
              placeholder="Longitude"
              value={destination.lng}
              onChange={(e) => setDestination(prev => ({ ...prev, lng: e.target.value }))}
              className="bg-slate-800/50 border-slate-700 text-sm"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleUseCurrentLocation('destination')}
              className="text-slate-400 hover:text-teal-400"
            >
              <Target className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Quick Fill */}
        <div className="flex gap-2 mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => quickFill('la_to_sd')}
            className="text-[10px] flex-1"
          >
            LA → San Diego
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => quickFill('sf_to_la')}
            className="text-[10px] flex-1"
          >
            SF → LA
          </Button>
        </div>

        {/* Analyze Button */}
        <Button
          onClick={handleAnalyze}
          disabled={loading}
          className="w-full bg-teal-600 hover:bg-teal-500"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing Route...
            </>
          ) : (
            <>
              <Navigation className="w-4 h-4 mr-2" />
              Analyze Route Safety
            </>
          )}
        </Button>

        {error && (
          <div className="mt-3 p-2 rounded bg-red-500/20 border border-red-500/50 text-red-400 text-xs">
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <ScrollArea className="flex-1 p-4">
          {/* Summary */}
          <div className="text-center mb-6 p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex items-center justify-center gap-4 mb-3">
              <div className={`w-16 h-16 rounded-full ${GRADE_COLORS[result.safety_summary?.grade] || 'bg-slate-500'} flex items-center justify-center`}>
                <span className="text-2xl font-bold text-white">
                  {result.safety_summary?.grade}
                </span>
              </div>
              <div className="text-left">
                <p className="text-2xl font-bold text-white">
                  {result.safety_summary?.average_score}
                </p>
                <p className="text-xs text-slate-400">Average Safety Score</p>
              </div>
            </div>
            
            <p className="text-sm text-slate-300">{result.safety_summary?.assessment}</p>
            
            {/* Score Range */}
            <div className="flex justify-between text-xs text-slate-500 mt-3">
              <span>Min: {result.safety_summary?.min_score}</span>
              <span>Max: {result.safety_summary?.max_score}</span>
            </div>
            <Progress 
              value={result.safety_summary?.average_score} 
              className="h-2 mt-1" 
            />
          </div>

          {/* Danger Zones Alert */}
          {result.danger_zone_count > 0 && (
            <div className="mb-6 p-3 rounded-lg bg-orange-500/20 border border-orange-500/50">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                <span className="text-sm font-medium text-orange-400">
                  {result.danger_zone_count} Danger Zone{result.danger_zone_count > 1 ? 's' : ''} Detected
                </span>
              </div>
              <p className="text-xs text-orange-300/80">
                Some segments of this route have elevated safety concerns
              </p>
            </div>
          )}

          {/* Time Context */}
          {result.time_of_day && (
            <div className="mb-6 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-teal-400" />
                <div>
                  <p className="text-sm text-slate-200">{result.time_of_day.period?.replace(/_/g, ' ').toUpperCase()}</p>
                  <p className="text-xs text-slate-500">{result.time_of_day.note}</p>
                </div>
              </div>
            </div>
          )}

          {/* Route Segments */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-teal-400" />
              Route Segments ({result.route?.total_segments || 0})
            </h4>
            
            <div className="space-y-0">
              {result.segments?.map((segment, index) => (
                <RouteSegment
                  key={index}
                  segment={segment}
                  index={index}
                  isLast={index === result.segments.length - 1}
                />
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {result.recommendations?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-teal-400" />
                Safety Recommendations
              </h4>
              {result.recommendations.map((rec, i) => (
                <RecommendationCard key={i} rec={rec} />
              ))}
            </div>
          )}

          {/* Analyzed At */}
          <p className="text-[10px] text-slate-600 text-center mt-4">
            Analyzed: {new Date(result.analyzed_at).toLocaleString()}
          </p>
        </ScrollArea>
      )}

      {/* Empty State */}
      {!result && !loading && (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <Navigation className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400">Enter origin and destination</p>
            <p className="text-xs text-slate-500 mt-1">Analyze safety along your travel route</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Dialog wrapper
export const RouteSafetyDialog = ({ trigger }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <Route className="w-4 h-4" />
            Route Safety
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md h-[85vh] p-0 glass-card border-slate-800">
        <RouteSafetyPlannerCard />
      </DialogContent>
    </Dialog>
  );
};

export default RouteSafetyPlannerCard;
